import type Owner from '@ember/owner';
import Route from '@ember/routing/route';
import type { Registry as Services } from '@ember/service';
import { service } from '@ember/service';
import { waitForProperty } from 'ember-concurrency';
import type HotelForecastController from 'lumen/controllers/hotel/forecast';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import { BudgetForecastDataFetching } from 'lumen/utils/budget-forecast/data-fetching';
import { LayoutSettingsHandler } from 'lumen/utils/budget-forecast/layout-settings';
import type { DateGranularity } from 'lumen/utils/constants/budget-forecast-constants';
import protectedRoute from 'lumen/utils/decorators/protected-route';

type ForecastParams = {
  endDate: string;
  entryId?: string;
  granularity: DateGranularity;
  startDate: string;
};

export type ForecastRouteModel = {
  capacities: typeof BudgetForecastDataFetching.prototype.requestCapacitiesTask;
  dataItems: typeof BudgetForecastDataFetching.prototype.requestDataItemsTask;
  entries: typeof BudgetForecastDataFetching.prototype.fetchEntriesTask;
  eventsAndHolidays: typeof BudgetForecastDataFetching.prototype.requestEventsAndHolidaysTask;
  segments: typeof BudgetForecastDataFetching.prototype.requestSegmentsTask;
};

// @ts-expect-error: Unable to resolve signature of class decorator when called as an expression.
@protectedRoute({
  features: 'app.business_intelligence.budget_and_forecast',
})
export default class HotelForecastRoute extends Route {
  queryParams = {
    startDate: { refreshModel: true },
    endDate: { refreshModel: true },
    granularity: { refreshModel: true },
    entryId: { refreshModel: true },
  };

  @service declare hotelAndMarket: HotelAndMarketServiceWithHotel;
  @service declare transition: Services['transition'];

  /**
   * Some requests depend only on the hotel.
   * Keep track of the last hotel ID to avoid unnecessary re-requests.
   */
  #cachedHotelId: string | undefined;
  #dataFetching: BudgetForecastDataFetching;
  #layoutSettingsHandler: LayoutSettingsHandler;

  constructor(owner: Owner) {
    super(owner);
    this.#dataFetching = new BudgetForecastDataFetching(owner);
    this.#layoutSettingsHandler = new LayoutSettingsHandler(owner);
  }

  deactivate(): void {
    this.#dataFetching.cancelAll();
    this.#dataFetching.unloadAll();

    this.#cachedHotelId = undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  model(params: ForecastParams, transition: Transition) {
    const model: ForecastRouteModel = {
      entries: this.#dataFetching.fetchEntriesTask,
      segments: this.#dataFetching.requestSegmentsTask,
      dataItems: this.#dataFetching.requestDataItemsTask,
      eventsAndHolidays: this.#dataFetching.requestEventsAndHolidaysTask,
      capacities: this.#dataFetching.requestCapacitiesTask,
    };

    void this.performModelTasks(model, params, transition);

    return model;
  }

  async performModelTasks(
    model: ForecastRouteModel,
    params: ForecastParams,
    transition: Transition,
  ): Promise<void> {
    const currentHotelId = this.hotelAndMarket.hotel.id!;
    const invalidateCache = this.#cachedHotelId !== currentHotelId;
    this.#cachedHotelId = currentHotelId;

    if (!(model.entries.last?.isSuccessful && !invalidateCache)) {
      await model.entries.perform('forecast');
    }

    if (model.entries.last?.value) {
      const isValid =
        this.transition.validateBudgetAndForecastEntryIdQueryParam(
          transition,
          model.entries.last.value,
        );

      if (!isValid) {
        return;
      }
    }

    void model.segments.perform({ entryId: params.entryId });
    void model.dataItems.perform({
      ...params,
      forecastEntryId: params.entryId,
    });
    void model.eventsAndHolidays.perform(params);
    void model.capacities.perform(params);
  }

  resetController(
    controller: HotelForecastController,
    isExiting: boolean,
  ): void {
    if (isExiting) {
      controller.dataTypes = [];
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  async setupController(
    controller: HotelForecastController,
    model: ForecastRouteModel,
    transition: Transition,
  ): Promise<void> {
    super.setupController(controller, model, transition);

    await waitForProperty(model.entries, 'isRunning', false);
    await waitForProperty(model.dataItems, 'isRunning', false);

    const data = model.dataItems.last?.value?.dataItems ?? [];
    const availableDataTypes = Array.from(
      new Set(data.map((item) => item.dataType)),
    ).filter((dataType) => dataType !== 'forecast');

    controller.dataTypes = ['forecast', ...availableDataTypes];

    controller.layoutSettings = this.#layoutSettingsHandler.getLayoutSettings({
      view: 'forecast',
      mode: 'view',
      availableDataTypes,
    });
  }
}
